import React from "react";
import moment from "moment";
import { makeStyles, Button, Link } from "@material-ui/core";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { useConfirmDialog, useNavManager, useSnackbars } from "@hooks";
import {
  BookingHospitalityGetQuery,
  useRemoveAccommodationDocumentMutation,
  BookingHospitalityGet,
  BookingScheduleSearch_Get,
  useToggleAccommodationConfirmationMutation
} from "@graphql";
import { DocumentList } from "@containers/DocumentList";
import { ThemeType } from "@theme";
import { OkCancelDialogOptions, UserAvatarChip } from "@containers";
import { PlainLink } from "@components";
import { WithCopyToClipboard } from "@common/with-copy-to-clipboard";
import { environment } from "../../../environments/environment";
import { BookingHospitalityDocumentSection } from "./components/BookingHospitalityDocumentSection";
import { AccommodationRequestCreateLocation } from "./locations/AccommodationRequestCreateLocation";
import { AccommodationRequestViewLocation } from "./locations/AccommodationRequestViewLocation";
import { AccommodationConfirmationCreateLocation } from "./locations/AccommodationConfirmationCreateLocation";
import { AccommodationRequest } from "./AccommodationRequest/AccommodationRequest";
import { AccommodationConfirmationViewLocation } from "./locations/AccommodationConfirmationViewLocation";
import { WeekYear } from "@bonliva-crm/common";

type Props = {
  bookingId: string;
  bookingGroupId: string;
  consultantName: string;
  data: BookingHospitalityGetQuery["bookingHospitality"];
  week: WeekYear;
};

export function BookingAccommodationSection(props: Props) {
  const { bookingId, data } = props;
  const classes = useStyles({});
  const { showError, showSuccess } = useSnackbars();
  const { confirm } = useConfirmDialog();
  const { navManager } = useNavManager();

  const [_removeDoc] = useRemoveAccommodationDocumentMutation({
    refetchQueries: [
      BookingHospitalityGet.OperationName,
      BookingScheduleSearch_Get.OperationName
    ]
  });

  const [_toggleConfirmation] = useToggleAccommodationConfirmationMutation({
    refetchQueries: [
      BookingHospitalityGet.OperationName,
      BookingScheduleSearch_Get.OperationName
    ]
  });

  const openRequestForm = () => {
    const request = AccommodationRequest.CreateNew(props.consultantName, "");

    navManager.open(
      "accommodation_request",
      new AccommodationRequestCreateLocation({
        bookingId: props.bookingId,
        formProps: {
          bookingGroupId: props.bookingGroupId,
          isDisabled: false,
          initialValues: request,
          isNew: true
        }
      })
    );
  };

  const openAccommodationConfirmation = (confirmationId: string) => {
    navManager.open(
      "accommodation_confirmation",
      new AccommodationConfirmationViewLocation({ bookingId, confirmationId })
    );
  };

  const openUploader = (files: File[]) => {
    store().navigation.open(
      "accomm_doc_uploader",
      new AccommodationConfirmationCreateLocation({
        bookingId: props.bookingId,
        files,
        week: props.week
      })
    );
  };

  const removeDoc = (fileId: string) =>
    confirm(new OkCancelDialogOptions()).then((res) => {
      if (res === "ok") {
        _removeDoc({ variables: { bookingId, fileId } })
          .then(() => showSuccess(locale.Deleted))
          .catch((err) => showError(`Cannot delete file\nReason: ${err.toString()}`));
      }
    });

  const toggleConfirmation = (id: string, isDeleted: boolean, weeks) => {
    if (!isDeleted) {
      confirm(
        new OkCancelDialogOptions(
          `${locale.AreYouSureRemoveConfirmation} ${weeks
            .map((x) => moment(x).format("YYYY.vW"))
            .join(", ")}`
        )
      ).then((res) => {
        if (res === "ok") {
          _toggleConfirmation({ variables: { bookingId, confirmationId: id } })
            .then(() => {
              showSuccess(locale.bookingSystemEvents.RemoveAccConfirmation);
            })
            .catch((err) => showError(err.message || err.toString()));
        }
      });
    } else {
      _toggleConfirmation({ variables: { bookingId, confirmationId: id } })
        .then(() => {
          showSuccess(locale.bookingSystemEvents.RestoreAccConfirmation);
        })
        .catch((err) => showError(err.message || err.toString()));
    }
  };

  const primaryButtonText =
    data.accommodation.accommodationRequest.length > 0
      ? "Beställ nytt boende"
      : "Beställ boende";

  let requestSentContent = null;

  if (data.accommodation.accommodationRequest.length > 0) {
    requestSentContent = (
      <div>
        {data.accommodation.accommodationRequest.map((request) => {
          const date = moment.utc(request.createdOn).format("L");

          return (
            <div
              key={request._id}
              style={{ padding: "4px 0", display: "flex", alignItems: "center" }}
            >
              <UserAvatarChip size="small" userName={request.createdByName} />
              <PlainLink
                style={{ marginLeft: 4 }}
                onClick={() =>
                  store().navigation.open(
                    "accommodation_request_view",
                    new AccommodationRequestViewLocation({ requestId: request._id })
                  )
                }
              >
                {`${locale.RequestedOn} ${date}`}
              </PlainLink>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <BookingHospitalityDocumentSection
      className={classes.item}
      title={locale.Accommodation}
      isOk={data.accommodation.accommodationIsOk}
      primaryButtonText={primaryButtonText}
      dropToUploadText="Dra filer hit eller klicka här för att ladda upp bokningsbekräftelse"
      switchText="Inget boende behövs"
      requestSent={data.accommodation.accommodationRequest.length > 0}
      requestSentContent={requestSentContent}
      onUpload={(files) => openUploader(files)}
      onMakeRequest={openRequestForm}
    >
      <div>
        {data.accommodation.confirmations.map((confirm) => {
          // ERP-owned mirrors are read-only in CRM — the row still opens the
          // read-only confirmation view, but cancel/restore and document
          // removal are owned by ERP and disabled here.
          const isErpOwned = confirm.source === "erp";
          // ERP-synced confirmations expose a direct link to their taxation
          // page in ERP so users who cannot open the confirmation can still
          // reach it from the table. Confirmations without a taxation page
          // (no taxationId) show no link.
          const erpTaxationLink =
            isErpOwned && confirm.taxationId
              ? `${environment.babErpUrl}/taxation/${confirm.taxationId}`
              : null;
          return (
            <React.Fragment key={confirm._id}>
              <div
                className={`${classes.docHeader} ${confirm.deleted ? "canceled" : ""}`}
              >
                <Link
                  component="button"
                  className={`${confirm.deleted ? "canceled" : ""}`}
                  variant="subtitle2"
                  color="secondary"
                  display="inline"
                  onClick={() =>
                    !confirm.deleted && openAccommodationConfirmation(confirm._id)
                  }
                >
                  {confirm.deleted ? `${locale.Deleted} - ` : ""}
                  {confirm.accommodationRef?.Name ?? locale.NoAccommodation}
                </Link>

                <div className={classes.controls}>
                  {erpTaxationLink && (
                    <WithCopyToClipboard contentToCopy={erpTaxationLink}>
                      <a
                        href={erpTaxationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.erpLink}
                      >
                        {locale.openInErp}
                      </a>
                    </WithCopyToClipboard>
                  )}
                  <UserAvatarChip size="small" userName={confirm.confirmedBy?.Name} />
                  <Button
                    variant={"outlined"}
                    disabled={isErpOwned}
                    onClick={() =>
                      toggleConfirmation(confirm._id, confirm.deleted, confirm.weeks)
                    }
                  >
                    {confirm.deleted ? locale.Restore : locale.Cancel}
                  </Button>
                </div>
              </div>

              <DocumentList
                className={classes.documentsWrapper}
                documents={confirm.documents}
                emptyMessage={
                  <span style={{ textAlign: "center" }}>
                    Inga boenden bekräftade för tillfället
                  </span>
                }
                onDelete={isErpOwned ? undefined : (doc) => removeDoc(doc.FileRef.NodeID)}
              />
            </React.Fragment>
          );
        })}
      </div>
    </BookingHospitalityDocumentSection>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  item: {
    width: "66%"
  },

  docHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 16px",
    background: "antiquewhite",
    borderTop: `1px solid ${theme.separatorColor}`,
    "&&.canceled": {
      background: "rgba(255,55,55, 1)",
      color: "#fff",
      opacity: 0.7
    },
    "&& a.canceled": {
      color: "#fff",
      pointerEvents: "none"
    }
  },

  documentsWrapper: {
    padding: 16
  },

  controls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0
  },

  erpLink: {
    whiteSpace: "nowrap",
    fontWeight: 600,
    color: theme.palette.primary.main
  }
}));
